<template>
  <div id="news-list">
    <Breadcrumbs
      :crumbs="[
        { name: mainStore.globals.info.school_name, link: `${mainStore.pathPrefix}/` },
        { name: mainStore.sectionName, link: `${mainStore.pathPrefix}/news` },
      ]"
    />
    <div class="page-container">
      <div
        v-if="filters && filters.length > 1"
        class="filter"
      >
        <select
          v-model="selectedFilter"
          name="filter"
          :aria-label="$t('pages.news.newsFilter')"
          @change="handleFilterChange"
        >
          <option
            v-for="filter in filters"
            :key="filter.id"
            :value="filter.id"
          >
            {{ filter.name }}
          </option>
        </select>
      </div>
      <LoadingIndicator v-if="isLoading" />
      <div
        v-for="article in news"
        v-else
        :key="article.id"
        class="page-container-item"
      >
        <div
          v-if="article.cover_image"
          class="article-image"
        >
          <img
            :alt="article.image_description || article.title"
            :src="article.cover_image"
          />
        </div>
        <div class="article-info">
          <div class="title">
            <h2>
              <nuxt-link :to="`${mainStore.pathPrefix}/article/${article.id}`">
                {{ replaceHTMLEntities(article.title) }}
              </nuxt-link>
            </h2>
          </div>
          <div class="article-date">
            {{ formatArticleDate(article) }}
          </div>
          <div class="content">
            {{ replaceHTMLEntities(article.snippet) }}
          </div>
          <div class="read-more">
            <nuxt-link
              :aria-label="$t('pages.news.readMoreAboutLabel', { title: article.title })"
              :to="`${mainStore.pathPrefix}/article/${article.id}`"
            >
              {{ $t('pages.news.readMoreUppercase') }} <span>&gt;</span>
            </nuxt-link>
          </div>
        </div>
      </div>

      <pagination
        v-if="totalPages > 1"
        :current-page="currentPage"
        :total-pages="totalPages"
      />
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue';
import { onBeforeRouteUpdate, useRoute } from 'vue-router';
import { useMainStore } from '@/stores/mainStore';
import { navigateTo, useAsyncData, useHead } from '#imports';

import Breadcrumbs from '../../components/global/Breadcrumbs.vue';
import LoadingIndicator from '../../components/global/LoadingIndicator.vue';
import Pagination from '../../components/global/Pagination.vue';
import { replaceHTMLEntities } from '../../helpers/strings';
import { getMetadataAndTags } from '../../helpers/metadata';
import { setupAndFetchArticles } from '../../helpers/section/sections.helper';
import { clientWorkWindowEvent } from '../../helpers/event-dispatch-helpers';

defineOptions({ name: 'NewsList' });

const mainStore = useMainStore();
const route = useRoute();
const { $axios, $d } = useNuxtApp();

const isLoading = ref(false);
const news = ref([]);
const filters = ref([]);
const selectedFilter = ref('');
const totalPages = ref(0);
const currentPage = ref(0);

const { data: initialData } = await useAsyncData('news', () => setupAndFetchArticles(mainStore, route.query, $axios));

if (initialData.value) {
  news.value = initialData.value.news ?? [];
  filters.value = initialData.value.filters ?? [];
  selectedFilter.value = initialData.value.selectedFilter ?? '';
  totalPages.value = initialData.value.totalPages ?? 0;
  currentPage.value = initialData.value.currentPage ?? 0;
}

const metaDataInfo = getMetadataAndTags(mainStore.settings.metadata, 'articles', mainStore.sectionName);

useHead({
  ...metaDataInfo.metaTitle,
  meta: metaDataInfo.metaTags,
});

watch(
  () => route.query.page_no,
  (newValue, oldValue) => {
    if (import.meta.client && oldValue !== newValue) {
      window.scrollTo(0, 0);
    }
  },
);

onBeforeRouteUpdate((to, from, next) => {
  if (import.meta.client && (to.query.page_no !== from.query.page_no || to.query.filter_id !== from.query.filter_id)) {
    fetchNews(to.query);
  }
  next();
});

onMounted(() => {
  clientWorkWindowEvent('news-list');
});

async function fetchNews(query) {
  isLoading.value = true;
  totalPages.value = 0;
  const result = await setupAndFetchArticles(mainStore, query, $axios);
  news.value = result.news;
  filters.value = result.filters;
  selectedFilter.value = result.selectedFilter;
  totalPages.value = result.totalPages;
  currentPage.value = result.currentPage;
  isLoading.value = false;
}

function handleFilterChange() {
  return navigateTo({ query: { filter_id: selectedFilter.value } });
}

function formatArticleDate(article) {
  if (!article.published_at) return '';
  return $d(article.published_at, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
</script>

<style scoped lang="scss">
#news-list {
  .article-image {
    width: 20%;
    float: left;
    height: auto;
    background: #fff;
    padding-right: 20px;
    img {
      width: 100%;
      height: auto;
    }
  }
  .article-info {
    margin-left: 2px;
    width: 75%;
    float: left;
    .title {
      margin-bottom: 10px;
      h2 {
        font-weight: 400;
        font-size: 24px;
        margin: 0;
      }
      a {
        color: var(--primary-color);
        border: none;
        text-decoration: none;
        &:hover {
          text-decoration: underline;
        }
      }
    }
    .article-date {
      font-size: 12px;
      color: #717171;
      margin-bottom: 10px;
    }
    .content {
      font-size: 14px;
      color: #717171;
      line-height: 28px;
      margin: 0;
    }
    .read-more {
      margin-top: 25px;
      a {
        color: var(--primary-color);
        text-decoration: none;
        font-size: 14px;
        span {
          display: inline-block;
          vertical-align: top;
        }
        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
}
</style>
